import React from 'react';
import {
    Calendar,
    ArrowRight,
    Building2,
    Store,
    Landmark,
    ShoppingCart,
    HeartPulse,
    ShieldCheck,
    Sliders,
    Rocket,
    Headphones,
    Star,
    ChevronRight
} from 'lucide-react';
import PublicPageLayout from '../../layouts/PublicPageLayout';

export default function SolutionsPage() {
    const solutionCards = [
        {
            icon: <Building2 className="w-5 h-5 text-blue-600" />,
            bgColor: 'bg-blue-50',
            title: 'Entreprises',
            description: 'Améliorez la productivité de vos équipes et la satisfaction de vos clients à grande échelle.',
            href: '/solutions/entreprises'
        },
        {
            icon: <Store className="w-5 h-5 text-emerald-600" />,
            bgColor: 'bg-emerald-50',
            title: 'PME',
            description: 'Une solution simple, rapide et abordable pour développer votre relation client.',
            href: '/solutions/pme'
        },
        {
            icon: <Landmark className="w-5 h-5 text-purple-600" />,
            bgColor: 'bg-purple-50',
            title: 'Banques & Finances',
            description: 'Assurez un service réactif et sécurisé pour vos clients avec une conformité maximale.',
            href: '/solutions/finances'
        },
        {
            icon: <ShoppingCart className="w-5 h-5 text-amber-600" />,
            bgColor: 'bg-amber-50',
            title: 'E-commerce',
            description: 'Gérez les demandes, retours et réclamations pour fidéliser vos clients en toute simplicité.',
            href: '/solutions/ecommerce'
        },
        {
            icon: <HeartPulse className="w-5 h-5 text-rose-600" />,
            bgColor: 'bg-rose-50',
            title: 'Santé & Assurance',
            description: 'Offrez un accompagnement personnalisé et humain à chaque étape du parcours client.',
            href: '/solutions/sante'
        }
    ];

    const advantages = [
        {
            icon: <ShieldCheck className="w-5 h-5 text-blue-600" />,
            title: 'Fiabilité',
            description: 'Une infrastructure robuste et sécurisée 99,99% du temps.'
        },
        {
            icon: <Sliders className="w-5 h-5 text-emerald-600" />,
            title: 'Flexibilité',
            description: 'Personnalisez la plateforme selon vos besoins métiers.'
        },
        {
            icon: <Rocket className="w-5 h-5 text-purple-600" />,
            title: 'Performance',
            description: 'Des outils puissants pour des équipes plus efficaces.'
        },
        {
            icon: <Headphones className="w-5 h-5 text-amber-600" />,
            title: 'Accompagnement',
            description: 'Un support dédié à chaque étape de votre croissance.'
        }
    ];

    return (
        <PublicPageLayout>
            <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-12">

                {/* ================= HERO SECTION ================= */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

                    {/* Côté Gauche : Titre + Subtitle + CTAs */}
                    <div className="lg:col-span-5 space-y-6">
                        <span className="text-xs font-bold text-blue-600 tracking-wider uppercase">
                            SOLUTIONS
                        </span>

                        <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 leading-tight">
                            Des solutions adaptées<br />
                            à chaque <span className="text-blue-600">entreprise.</span>
                        </h1>

                        <p className="text-slate-500 text-xs sm:text-sm leading-relaxed max-w-md">
                            IZI Call Center aide les entreprises de toutes tailles à offrir une expérience client exceptionnelle grâce à une plateforme flexible, sécurisée et évolutive.
                        </p>

                        <div className="flex flex-wrap items-center gap-4 pt-1">
                            <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-5 py-3 rounded-xl transition-all flex items-center gap-2 shadow-md shadow-blue-500/20">
                                <Calendar size={15} />
                                <span>Réserver une démo</span>
                            </button>

                            <a href="#solutions-list" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors">
                                <span>Voir toutes les solutions</span>
                                <ArrowRight size={14} />
                            </a>
                        </div>
                    </div>

                    {/* Côté Droit : Illustration Schéma Réseau / Solution */}
                    <div className="lg:col-span-7 flex justify-center items-center">
                        <img
                            src="/solutions/hero.png"
                            alt="Écosystème des solutions IZI Call Center"
                            className="w-full h-auto max-h-[420px] object-contain"
                        />
                    </div>

                </div>

                {/* ================= GRILLE DES SOLUTIONS (5 CARTES) ================= */}
                <div id="solutions-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {solutionCards.map((card, idx) => (
                        <a
                            key={idx}
                            href={card.href}
                            className="bg-white rounded-2xl p-5 border border-slate-200/70 shadow-sm flex flex-col justify-between hover:shadow-md hover:border-slate-300 transition-all group relative overflow-hidden"
                        >
                            <div className="space-y-3">
                                <div className={`w-10 h-10 rounded-xl ${card.bgColor} flex items-center justify-center shrink-0`}>
                                    {card.icon}
                                </div>

                                <h3 className="text-xs font-extrabold text-slate-900">{card.title}</h3>

                                <p className="text-[11px] text-slate-500 leading-relaxed">
                                    {card.description}
                                </p>
                            </div>

                            <div className="flex justify-end pt-4">
                                <div className="w-7 h-7 rounded-full bg-slate-50 text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600 flex items-center justify-center transition-colors">
                                    <ChevronRight size={14} />
                                </div>
                            </div>
                        </a>
                    ))}
                </div>

                {/* ================= SECTION : POURQUOI CHOISIR IZI ================= */}
                <div className="bg-white rounded-3xl border border-slate-200/70 shadow-sm overflow-hidden grid grid-cols-1 lg:grid-cols-12 items-center">

                    {/* Contenu Gauche (Titre + 4 Avantages) */}
                    <div className="lg:col-span-8 px-6 lg:px-6 space-y-8">
                        <h2 className="text-lg lg:text-xl font-extrabold text-slate-900">
                            Pourquoi les entreprises choisissent IZI Call Center ?
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {advantages.map((adv, idx) => (
                                <div key={idx} className="space-y-2 border-l-2 border-slate-100 pl-3">
                                    <div className="pb-1">{adv.icon}</div>
                                    <h3 className="text-xs font-extrabold text-slate-900">{adv.title}</h3>
                                    <p className="text-[11px] text-slate-500 leading-relaxed">
                                        {adv.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Image Droite (Opératrice + Note Avis) */}
                    <div className="lg:col-span-4 bg-blue-50/30 px-2 lg:p-0 flex justify-center items-end relative h-full min-h-[180px]">
                        <img
                            src="/solutions/support-agent.png"
                            alt="Conseillère IZI Call Center"
                            className="w-full max-w-[280px] h-full object-contain"
                        />

                        {/* Badge d'Avis Clients */}
                        <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-2xl p-3 border border-slate-200/80 shadow-lg flex items-center gap-2.5">
                            <div className="flex text-amber-400">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={12} fill="currentColor" />
                                ))}
                            </div>
                            <div>
                                <div className="text-[11px] font-extrabold text-slate-900">4.9/5</div>
                                <div className="text-[9px] text-slate-500">+2,000 avis clients</div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </PublicPageLayout>
    );
}